import * as React from "react";
import {useEffect, useState} from "react";
import styled from "styled-components";

export const AD_ROTATION_INTERVAL = 15000;

export interface IAdCreative {
	readonly href: string;
	readonly src: string;
	readonly alt?: string;
	readonly mobile?: {
		readonly href?: string;
		readonly src: string;
	};
	readonly tracking_pixel?: string;
}

const AdLink = styled.a`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const TrackingPixel = styled.img`
	border: 0;
	height: 1px;
	max-height: 1px;
	width: 1px;
	opacity: 0;
`;

interface IAdLinkProps {
	readonly creative: IAdCreative;
	readonly href: string;
	readonly src: string;
	readonly className?: string;
	readonly with_pixel?: boolean;
}

const AdCreativeLink = ({
	creative,
	href,
	src,
	className,
	with_pixel,
}: IAdLinkProps) => (
	<AdLink
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		className={className}
	>
		<img src={src} alt={creative.alt || "Ad"} />
		{with_pixel && creative.tracking_pixel ? (
			<TrackingPixel src={creative.tracking_pixel} alt="Advertisement" />
		) : null}
	</AdLink>
);

interface IProps {
	readonly creatives: IAdCreative[];
	readonly interval?: number;
	readonly className?: string;
}

export const RotatingAd = ({
	creatives,
	interval = AD_ROTATION_INTERVAL,
	className,
}: IProps) => {
	const [index, setIndex] = useState(0);
	const total = creatives.length;

	useEffect(() => {
		if (total < 2) {
			return undefined;
		}

		const timer = window.setInterval(
			() => setIndex((current) => (current + 1) % total),
			interval
		);

		return () => window.clearInterval(timer);
	}, [total, interval]);

	if (!total) {
		return null;
	}

	const creative = creatives[index % total];

	if (!creative.mobile) {
		return (
			<AdCreativeLink
				creative={creative}
				href={creative.href}
				src={creative.src}
				className={className}
				with_pixel={true}
			/>
		);
	}

	return (
		<React.Fragment>
			<AdCreativeLink
				creative={creative}
				href={creative.href}
				src={creative.src}
				className={`hidden-mobile ${className || ""}`.trim()}
				with_pixel={true}
			/>
			<AdCreativeLink
				creative={creative}
				href={creative.mobile.href || creative.href}
				src={creative.mobile.src}
				className={`hidden-desktop ${className || ""}`.trim()}
			/>
		</React.Fragment>
	);
};

export default RotatingAd;
